import {
  Briefcase,
  Clock,
  DocumentBriefcase,
  DocumentCopy,
  DocumentText,
  DollarCircle,
  Edit,
} from '../icons';
import CardDetailButton from '../cardDetailButton/cardDetailButton';
import InteractiveCard from '../common/interactiveCard';
import { useTranslation } from 'react-i18next';

import { ITransaction } from '../../interface/transaction';
import { SystemConstants, ROUTES } from '../../constants/constants';
import { useTransactionParameters } from '../../context/TransactionParameterContext';
import {
  formatter,
  getMatterIconClass,
  getMatterLabel,
  getTotalPrice,
  getUnitPrice,
} from '../../constants/utilityFunctions';
import {
  cardBorderColorMapping,
  InvoiceableType,
  tagColorMapping,
  TransactionStatus,
} from '../../constants/transaction';
import { Divider, IconButton, Paragraph, Text, Tooltip } from '../Ant';
import CardData from './cardData';
import { Flex } from 'antd';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { HoursDurationDisplay } from '../hoursDurationDisplay/hoursDurationDisplay';
import NoteIcons from '../common/NoteIcons';

const TransactionCard = ({
  transaction,
  hidden,
  onEdit,
  onCopy,
  onNewToSameMatter,
  childListCard = false,
}: {
  transaction: ITransaction;
  hidden?: boolean;
  onEdit?: (transaction: ITransaction) => void;
  onCopy?: (transaction: ITransaction) => void;
  onNewToSameMatter?: (transaction: ITransaction) => void;
  childListCard?: boolean;
}) => {
  const { t } = useTranslation();
  const { getAppLanguageCode } = useLanguage();
  const { push } = useNavigationStack();
  const { parameters } = useTransactionParameters();
  const unitTranslationName =
    transaction?.unit?.translations?.find((t) => t.language === getAppLanguageCode())
      ?.translation ?? '-';
  const showEdit = [TransactionStatus.nonBillable, TransactionStatus.open].includes(
    transaction?.status,
  );
  const handleTransactionView = () => {
    push(`${ROUTES.TRANSACTION_VIEW}/${transaction?.id}`);
  };
  const baseCurrency = parameters?.currency;
  const handleEditTranasction = (transaction: ITransaction) => {
    onEdit?.(transaction);
  };

  return (
    <InteractiveCard
      data-testid="transaction-card"
      className={`pointer w-full ${cardBorderColorMapping[transaction?.status]}`}
      onActivate={() => handleTransactionView()}
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={5} align="center">
          <Flex
            justify="center"
            align="center"
            data-testid="transaction-status-chip"
            className={`status-chip ${tagColorMapping[transaction?.status]}`}
          >
            {transaction?.status &&
              t(
                `transaction:status.${Object.keys(TransactionStatus).find((key) => TransactionStatus[key as keyof typeof TransactionStatus] === transaction?.status) ?? transaction?.status}`,
              )}
          </Flex>
          <NoteIcons
            hasImportantNotes={transaction?.hasImportantNotes}
            hasNotes={transaction?.hasNotes}
          />
        </Flex>
        <Flex gap={10}>
          <Tooltip title={t('transaction:newToSameMatter')} placement="top" arrow={false}>
            <IconButton
              data-testid="new-to-same-matter-button"
              aria-label={t('transaction:newToSameMatter')}
              icon={<DocumentBriefcase />}
              onClick={(e) => {
                e.stopPropagation();
                onNewToSameMatter?.(transaction);
              }}
            />
          </Tooltip>
          <Tooltip title={t('transaction:copyTransaction')} placement="top" arrow={false}>
            <IconButton
              data-testid="copy-transaction-button"
              aria-label={t('transaction:copyTransaction')}
              icon={<DocumentCopy />}
              onClick={(e) => {
                e.stopPropagation();
                onCopy?.(transaction);
              }}
            />
          </Tooltip>

          {showEdit && (
            <Tooltip title={t('common:edit')} placement="top" arrow={false}>
              <IconButton
                data-testid="edit-transaction-button"
                aria-label={t('common:edit')}
                icon={<Edit />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditTranasction(transaction);
                }}
              />
            </Tooltip>
          )}

          <CardDetailButton onOpen={handleTransactionView} testId="go-detail-button" />
        </Flex>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10}>
        {!childListCard && (
          <CardData
            type="matter"
            icon={
              <Briefcase
                className={getMatterIconClass(transaction?.matter?.invoiceableType ?? '')}
              />
            }
            title={getMatterLabel(t, transaction?.matter?.invoiceableType)}
            content={
              transaction?.principalCompany?.name
                ? transaction?.principalCompany?.name
                : (transaction?.principalPrivatePerson?.fullName ?? '')
            }
            subcontent={transaction?.matter?.matterNumber + ' - ' + transaction?.matter?.subject}
            subcontentclass="lineclamp-1"
            contentclass="lineclamp-1"
          />
        )}
        <CardData
          icon={<DocumentText />}
          type="transaction"
          title={t('transaction:transaction')}
          subcontent={transaction?.subject ?? ''}
          subcontentclass="lineclamp-2"
        />
        {childListCard && (
          <CardData
            type="author"
            icon={<DocumentText />}
            title={t('common:author')}
            content={transaction?.transactionUser?.fullName ?? ''}
            contentclass="lineclamp-2"
          />
        )}

        <>
          {!childListCard && <Divider />}
          <Flex justify="space-between">
            {transaction?.unitId && transaction?.unitId === SystemConstants.UNIT_HOUR ? (
              <HoursDurationDisplay
                title={t('transaction:billableHours')}
                durationInHours={transaction?.billableQuantity ?? 0}
              />
            ) : (
              <CardData
                type="hours"
                icon={<Clock />}
                title={t('transaction:billableAmount') + ` (${unitTranslationName})`}
                content={formatter.regionalNumberFormat({
                  value: transaction?.billableQuantity ?? 0,
                  minimumFractionDigits: 2,
                })}
              />
            )}
            {!hidden && transaction?.matter?.invoiceableType !== InvoiceableType.INTERNAL && (
              <CardData
                type="price"
                icon={<DollarCircle />}
                title={
                  (transaction?.unitId === SystemConstants.UNIT_HOUR
                    ? t('transaction:hourPrice')
                    : t('transaction:unitPrice')) + ` (${transaction?.matter?.currency})`
                }
                content={getUnitPrice(baseCurrency ?? '', transaction)}
              />
            )}
          </Flex>
          {!hidden && transaction?.matter?.invoiceableType !== InvoiceableType.INTERNAL && (
            <>
              <Divider />
              <Flex justify="space-between" align="center">
                <Paragraph
                  data-testid="total-price-title"
                  $fontsize="12px"
                  $color="title"
                  $weight={500}
                  $lineheight="20px"
                >
                  {t('transaction:totalPrice')} {`(${transaction?.matter?.currency})`}
                </Paragraph>
                <Text
                  data-testid="total-price"
                  $fontsize="13px"
                  $color="title"
                  $weight={700}
                  $lineheight="17px"
                >
                  {getTotalPrice(baseCurrency ?? '', transaction)}
                </Text>
              </Flex>
            </>
          )}
        </>
      </Flex>
    </InteractiveCard>
  );
};

export default TransactionCard;
